import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { adminGetPartner, adminUpdatePartner, adminMergePartner, adminDeletePartner, adminRestorePartner } from '../api';
import { Section, TextInput, NumberInput, Textarea } from '../components/product-edit/ui';

const STATE_LABELS = {
  pending_payment: 'Pend. pago', paid: 'Pagado', processing: 'En proceso',
  shipped: 'Enviado', delivered: 'Entregado', cancelled: 'Cancelado',
};

const errMsg = e => e?.response?.data?.detail || 'No se pudo completar la acción';

const PartnerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [mergeId, setMergeId] = useState('');
  const [msg, setMsg] = useState('');

  const { data: partner, isLoading, error } = useQuery({
    queryKey: ['admin-partner', id],
    queryFn: () => adminGetPartner(id),
    staleTime: 0,
  });

  useEffect(() => {
    if (partner) {
      setForm({ name: partner.name ?? '', phone: partner.phone ?? '', email: partner.email ?? '', notes: partner.notes ?? '' });
    }
  }, [partner]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-partner', id] });
    qc.invalidateQueries({ queryKey: ['admin-partners'] });
  };

  const save = useMutation({
    mutationFn: () => adminUpdatePartner(id, {
      name: form.name || null, phone: form.phone || undefined, email: form.email || null, notes: form.notes || null,
    }),
    onSuccess: () => { setMsg('Guardado'); refresh(); },
    onError: e => setMsg(errMsg(e)),
  });

  const remove = useMutation({
    mutationFn: () => adminDeletePartner(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin-partners'] }); navigate('/admin/clientes'); },
    onError: e => setMsg(errMsg(e)),
  });
  const restore = useMutation({
    mutationFn: () => adminRestorePartner(id),
    onSuccess: () => { setMsg(''); refresh(); },
    onError: e => setMsg(errMsg(e)),
  });

  const merge = useMutation({
    mutationFn: () => adminMergePartner(id, parseInt(mergeId, 10)),
    onSuccess: data => { qc.invalidateQueries({ queryKey: ['admin-partners'] }); navigate(`/admin/clientes/${data.id}`); },
    onError: e => setMsg(errMsg(e)),
  });

  if (isLoading || !form) {
    return <div className="p-8 font-mono text-[11px] uppercase tracking-widest text-brand-faint">Cargando…</div>;
  }
  if (error || !partner) return <div className="p-8 text-brand-muted">Cliente no encontrado.</div>;

  const set = (k, v) => { setMsg(''); setForm(f => ({ ...f, [k]: v })); };

  const doRemove = () => {
    if (window.confirm('La ficha se oculta de la lista (se puede restaurar). Sus pedidos no se borran. ¿Eliminar?')) remove.mutate();
  };

  const doMerge = () => {
    const n = parseInt(mergeId, 10);
    if (!n) return;
    if (window.confirm(`Esta ficha desaparece y sus pedidos pasan al cliente #${n}. ¿Continuar?`)) merge.mutate();
  };

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto">
      <button onClick={() => navigate('/admin/clientes')}
        className="mb-4 inline-flex items-center gap-1.5 text-[12px] text-brand-muted hover:text-brand-text">
        <ArrowLeft size={13} /> Clientes
      </button>
      <h1 className="font-display text-2xl font-semibold text-brand-text tracking-tight">
        {partner.name || 'Cliente sin nombre'} <span className="font-mono text-[12px] text-brand-faint">#{partner.id}</span>
      </h1>
      <p className="mt-1 text-[12px] text-brand-muted">
        {partner.orders_count} pedidos · S/ {partner.total_spent.toFixed(2)} comprados
        {partner.created_at && ` · cliente desde ${new Date(partner.created_at).toLocaleDateString('es-PE')}`}
      </p>

      {partner.deleted_at && (
        <div className="mt-4 flex items-center justify-between rounded border border-brand-red/40 px-4 py-3 text-[13px] text-brand-red">
          <span>Cliente eliminado el {new Date(partner.deleted_at).toLocaleDateString('es-PE')}. No se puede editar.</span>
          <button type="button" onClick={() => restore.mutate()} disabled={restore.isPending}
            className="rounded border border-brand-accent/50 px-3 py-1 text-[12px] text-brand-accent">Restaurar</button>
        </div>
      )}

      <div className="mt-8 grid gap-10 md:grid-cols-2">
        <div className="space-y-8">
          <Section title="Datos del cliente">
            <div className="space-y-5">
              <TextInput label="Nombre" value={form.name} onChange={e => set('name', e.target.value)} />
              <TextInput label="Teléfono" value={form.phone} onChange={e => set('phone', e.target.value)} />
              {partner.phone && (
                <a href={`https://wa.me/${partner.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer"
                  className="block text-[12px] text-brand-accent underline">Abrir WhatsApp</a>
              )}
              <TextInput label="Correo" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
              <Textarea label="Notas internas" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} />
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => save.mutate()} disabled={save.isPending || !!partner.deleted_at}
                  className="inline-flex items-center gap-2 rounded bg-brand-accent px-4 py-2 text-[12px] font-medium text-black disabled:opacity-50">
                  {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Guardar
                </button>
                {msg && <span className={`text-[12px] ${msg === 'Guardado' ? 'text-brand-accent' : 'text-brand-red'}`}>{msg}</span>}
              </div>
            </div>
          </Section>

          {!partner.deleted_at && (
            <Section title="Eliminar cliente">
              <div className="space-y-3">
                <p className="text-[12px] text-brand-muted">Oculta la ficha de la lista. Se puede restaurar y sus pedidos no se tocan. Si el mismo teléfono vuelve a pedir, la ficha se reactiva sola.</p>
                <button type="button" onClick={doRemove} disabled={remove.isPending}
                  className="rounded border border-brand-red/50 px-3 py-1.5 text-[12px] text-brand-red">Eliminar cliente</button>
              </div>
            </Section>
          )}

          <Section title="Unir con otra ficha">
            <div className="space-y-4">
              <p className="text-[12px] text-brand-muted">
                Si es la misma persona con otro teléfono, escribe el # del cliente que se queda. Los pedidos de esta
                ficha pasan a ese cliente y esta ficha se elimina.
              </p>
              <NumberInput label="# del cliente destino" min="1" value={mergeId} onChange={e => setMergeId(e.target.value)} />
              <button type="button" onClick={doMerge} disabled={!mergeId || merge.isPending}
                className="rounded border border-brand-red/50 px-3 py-1.5 text-[12px] text-brand-red disabled:opacity-40">
                Unir y eliminar esta ficha
              </button>
            </div>
          </Section>
        </div>

        <Section title="Pedidos">
          {partner.orders.length === 0 ? (
            <p className="text-[13px] text-brand-faint">Sin pedidos.</p>
          ) : (
            <ul className="divide-y divide-brand-border-md">
              {partner.orders.map(o => (
                <li key={o.id} onClick={() => navigate(`/admin/ordenes/${o.id}`)}
                  className="flex cursor-pointer items-center justify-between py-3 text-[13px] hover:bg-brand-surface/50">
                  <div>
                    <p className="font-mono text-brand-text">{o.reference}</p>
                    <p className="text-[11px] text-brand-faint">
                      {new Date(o.created_at).toLocaleDateString('es-PE')} · {STATE_LABELS[o.state] ?? o.state}
                    </p>
                  </div>
                  <span className="text-brand-text">S/ {o.total.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
};

export default PartnerDetailPage;
